import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import trim from 'lodash/trim';
import { useResizeX } from 'Hooks/useResize';
import useWindowDimensions from 'Hooks/useWindowSize';
import { useIntl } from 'react-intl';
import { selectSchedulerDetail, selectUserAgents } from 'store/Schedule/selectors';
import { resetDetail } from 'store/Schedule/actions';
import StandardDropdown from 'Common/Components/StandarDropdown';
import Header from './Header';
import StyledCheckbox from 'Common/Components/StyledCheckbox';
import CronExpression from '../CreateEditSchedule/components/CronExpression';
import AgentsDropdown from '../CreateEditSchedule/components/AgentsDropdown';
import ToolParams from 'Screens/Automation/Agents/components/AgentDetail/components/ToolParams';
import WorkspacesSelector from '../CreateEditSchedule/components/WorkspacesSelector';
import {
  ResizerWrapper, Resizer, Transparency
} from 'Screens/Contextualization/Host/components/HostDetail/styled';
import {
  Subtitle, WrapperAgent, AgentSection,
  DescriptionSection, Label, Description
} from 'Screens/Automation/Schedule/components/CreateEditSchedule/styled';
import { Wrapper, DetailWrapper, Content, StyledToolParams } from './styled';

const SchedulerDetail = () => {
  const dispatch = useDispatch();
  const [showTransparency, setShowTransparency] = useState(false);
  const ref = useRef(null);
  const wrapperRef = useRef(null);
  const resizerRef = useRef(null);
  const { width } = useWindowDimensions();
  const minWidth = width < 3000 ? width * 0.55 : width * 0.35;
  const maxWidth = width < 3000 ? width * 0.75 : width * 0.55;
  const wrapperWidth = useResizeX(wrapperRef, resizerRef, minWidth, minWidth, maxWidth);
  const schedule = useSelector(selectSchedulerDetail);
  const userAgents = useSelector(selectUserAgents);
  const [dataToSave, setData] = useState({
    scheduleId: get(schedule, 'value.id', 0),
    crontab: {
      expression: get(schedule, 'value.crontab', ''),
      name: ''
    },
    active: get(schedule, 'value.active', false),
    description: get(schedule, 'value.description', ''),
    currentAgent: { label: get(schedule, 'value.agent.name', ''), value: { ...get(schedule, 'value.agent', {}) }, type: 'agent' },
    tool: '',
    parameters: get(schedule, 'value.parameters', {}),
    workspaces: get(schedule, 'value.workspaces', []),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    type: 'agent',
    targetError: ''
  });
  const tools = get(dataToSave, 'currentAgent.value.executors', []);
  const mappedTools = tools.map((t) => ({ name: t.name, desc: t.name }));

  const intl = useIntl();

  const onScroll = () => {
    const scrollTop = get(ref, 'current.scrollTop', 0);
    if (scrollTop === 0) setShowTransparency(false);
    else setShowTransparency(true);
  };

  useEffect(() => {
    setData({
      scheduleId: get(schedule, 'value.id', 0),
      crontab: {
        expression: get(schedule, 'value.crontab', ''),
        name: ''
      },
      active: get(schedule, 'value.active', false),
      description: get(schedule, 'value.description', ''),
      currentAgent: { label: get(schedule, 'value.agent.name', ''), value: { ...get(schedule, 'value.agent', {}) }, type: 'agent' },
      tool: '',
      parameters: get(schedule, 'value.parameters', {}),
      workspaces: get(schedule, 'value.workspaces', []),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      type: 'agent',
      targetError: ''
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedule]);

  useEffect(() => {
    const toolName = get(schedule, 'value.executor.name', '');
    const tool = get(schedule, 'value.agent.executors')?.find((t) => t.name === toolName);
    const defaultCrontab = () => {
      let crontabName = '';
      const crontabValue = get(schedule, 'value.crontab', '');
      switch (crontabValue) {
        case '0 4 * * *':
          crontabName = 'day';
          break;
        case '0 4 * * 0':
          crontabName = 'week';
          break;
        case '0 4 1 * *':
          crontabName = 'month';
          break;
        default:
          crontabName = 'custom';
          break;
      }
      return { name: crontabName, expression: crontabValue };
    };
    setData({ ...dataToSave, tool, crontab: defaultCrontab() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => {
    dispatch(resetDetail());
  }, [dispatch]);

  const onChange = (field, value) => {
    const newData = { ...dataToSave, [field]: value };
    setData(newData);
  };
  const parsedAgents = () => {
    if (!isEmpty(userAgents)) return userAgents.map((t) => ({ label: get(t, 'name', ''), value: t }));
    else return [];
  };

  const onChangeAgent = (obj) => {
    const newAgent = { ...obj, type: 'agent' };
    const newData = { ...dataToSave, currentAgent: newAgent, tool: {}, parameters: {} };
    setData(newData);
  };

  const onChangeField = (field, value) => {
    const newParams = { ...dataToSave.parameters };
    newParams[field] = value;
    if (!trim(value)) delete newParams[field];
    onChange('parameters', newParams);
  };

  const onChangeTool = (field, value) => {
    const tool = get(dataToSave, 'currentAgent.value.executors', get(schedule, 'value.agent.executors')).find((t) => t.name === value);
    const newData = { ...dataToSave, tool, parameters: {} };
    setData(newData);
  };

  return schedule
    ? (
    <Wrapper ref={ wrapperRef } wrapperWidth={ wrapperWidth }>
      <ResizerWrapper ref={ resizerRef } children={ <Resizer /> } />
      <DetailWrapper>
        <Header dataToSave={ dataToSave } />
        { showTransparency && <Transparency /> }
        <Content tabRef={ ref } onScroll={ onScroll } showTransparency={ showTransparency }>
          <DescriptionSection>
            <Label>{ intl.formatMessage({ id: 'schedule.createEditSchedule.description' }) }</Label>
            <Description id="scheduler-modal-description" onChange={ (e) => onChange('description', e.target.value) } value={ dataToSave.description } placeholder={ intl.formatMessage({ id: 'schedule.createEditSchedule.description.placeholder' }) } />
            <StyledCheckbox id="scheduler-modal-active" checked={ dataToSave.active } label="Active" onChange={ () => { onChange('active', !dataToSave.active); } } />
          </DescriptionSection>

          <CronExpression crontab={ dataToSave.crontab } onChangeCrontab={ (value) => onChange('crontab', value) } />

          <AgentSection>
            <Subtitle>{ intl.formatMessage({ id: 'schedule.createEditSchedule.agentSelection' }) }</Subtitle>
            <WrapperAgent>
            <AgentsDropdown id="scheduler-modal-agent" options={ parsedAgents() } updateValue={ onChangeAgent } value={ dataToSave.currentAgent } placeholder={ intl.formatMessage({ id: 'schedule.createEditSchedule.dropdownAgent.placeholder' }) } />
            </WrapperAgent>

            { (!isEmpty(dataToSave.currentAgent) || !isEmpty(get(schedule, 'value.agent', null))) &&
              (<>
                  <Subtitle>{ intl.formatMessage({ id: 'schedule.createEditSchedule.toolSelection' }) }</Subtitle>
                  <StandardDropdown id="scheduler-modal-tool" defaultValue={ get(dataToSave, 'tool.name', '') } width="200px" field="toolParams" options={ mappedTools } updateValue={ onChangeTool } placeholder={ intl.formatMessage({ id: 'schedule.createEditSchedule.dropdownTool.placeholder' }) } />
                  { dataToSave?.tool?.name
                    ? <StyledToolParams>
                        <ToolParams currentTool={ dataToSave.tool } defaultParams={ dataToSave.parameters } onChangeField={ onChangeField } />
                      </StyledToolParams>
                    : null
                  }
               </>
              ) }
          </AgentSection>
          <WorkspacesSelector scheduleWorkspaces={ dataToSave.workspaces } setScheduleWorkspaces={ (items) => onChange('workspaces', items) } />
        </Content>
      </DetailWrapper>
    </Wrapper>
      )
    : null;
};

export default SchedulerDetail;
